import { useEffect, useState, ReactNode } from "react";
import { Link } from "react-router-dom";
import { z } from "zod";
import { BookOpen, ArrowRight, CheckCircle2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { BOOKS, BookKey, getBook, GHL_BOOK_WEBHOOK_URL } from "@/config/books";
import AppointmentDialog from "./AppointmentDialog";

const STORAGE_KEY = "jm_book_contact";

const schema = z.object({
  firstName: z.string().trim().min(1, "First name is required").max(60),
  lastName: z.string().trim().min(1, "Last name is required").max(60),
  email: z.string().trim().email("Enter a valid email").max(255),
  phone: z
    .string()
    .trim()
    .max(30)
    .refine((v) => v === "" || (/^[0-9+\-()\s.]+$/.test(v) && v.replace(/\D/g, "").length >= 7), {
      message: "Enter a valid phone, or leave it blank",
    }),
  book: z.enum(["fsbo", "expired", "top-dollar", "buying", "downsize"]),
  smsConsent: z.boolean(),
});

type Values = z.infer<typeof schema>;

const readSaved = (): Partial<Values> => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const save = (v: Values) => {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ firstName: v.firstName, lastName: v.lastName, email: v.email, phone: v.phone }),
    );
  } catch {
    /* storage unavailable */
  }
};

/** Send the request to John's GHL inbound webhook. Never blocks the visitor from the book. */
async function sendToGhl(payload: Record<string, unknown>): Promise<void> {
  if (!GHL_BOOK_WEBHOOK_URL) return;
  const body = JSON.stringify(payload);
  try {
    const res = await fetch(GHL_BOOK_WEBHOOK_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    });
    if (!res.ok) console.warn("[book-request] webhook responded", res.status);
  } catch {
    // CORS blocked the JSON request before it was sent: retry as a simple request.
    try {
      await fetch(GHL_BOOK_WEBHOOK_URL, { method: "POST", mode: "no-cors", body, keepalive: true });
    } catch (err) {
      console.warn("[book-request] webhook failed", err);
    }
  }
}

interface Props {
  trigger: ReactNode;
  defaultBook: BookKey;
}

const BookRequestDialog = ({ trigger, defaultBook }: Props) => {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [doneBook, setDoneBook] = useState<BookKey | null>(null);
  const [errors, setErrors] = useState<Partial<Record<keyof Values, string>>>({});
  const [form, setForm] = useState<Values>({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    book: defaultBook,
    smsConsent: false,
  });

  useEffect(() => {
    if (!open) return;
    const saved = readSaved();
    setDoneBook(null);
    setErrors({});
    setForm((f) => ({ ...f, ...saved, book: defaultBook, smsConsent: false }));
  }, [open, defaultBook]);

  const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const t = e.target as HTMLInputElement;
    const value = t.type === "checkbox" ? t.checked : t.value;
    setForm({ ...form, [t.name]: value });
    if (errors[t.name as keyof Values]) setErrors({ ...errors, [t.name]: undefined });
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = schema.safeParse(form);
    if (!parsed.success) {
      const f: Partial<Record<keyof Values, string>> = {};
      for (const i of parsed.error.issues) {
        const k = i.path[0] as keyof Values;
        if (!f[k]) f[k] = i.message;
      }
      setErrors(f);
      return;
    }
    const v = parsed.data;
    const book = getBook(v.book);
    setSubmitting(true);
    save(v);
    await sendToGhl({
      first_name: v.firstName,
      last_name: v.lastName,
      full_name: `${v.firstName} ${v.lastName}`,
      email: v.email,
      phone: v.phone || "",
      requested_book: book.title,
      book_key: book.key,
      book_url: book.readUrl,
      tags: ["website-book-request", book.tag],
      sms_consent: v.phone ? v.smsConsent : false,
      source: "johnsellsnj.realestate",
      page_url: window.location.href,
      submitted_at: new Date().toISOString(),
    });
    setSubmitting(false);
    setDoneBook(v.book);
  };

  const inputCls =
    "w-full bg-transparent border-b border-border focus:border-primary outline-none py-2.5 text-foreground transition-colors";
  const labelCls = "text-[11px] font-semibold tracking-[0.22em] uppercase text-foreground mb-1.5 block";
  const errorCls = "text-xs text-destructive mt-1";
  const done = doneBook ? getBook(doneBook) : null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        {done ? (
          <div className="text-center py-2">
            <CheckCircle2 className="h-10 w-10 text-primary mx-auto mb-4" strokeWidth={1.25} />
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl md:text-3xl font-light leading-tight text-center">
                Your free digital copy is ready.
              </DialogTitle>
              <DialogDescription className="text-muted-foreground font-light text-center">
                Thanks, {form.firstName}. Open <span className="italic">{done.title}</span> below. John may follow up
                to see if you have any questions.
              </DialogDescription>
            </DialogHeader>
            <a
              href={done.readUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center justify-center gap-3 bg-primary text-primary-foreground px-8 py-4 text-xs tracking-[0.3em] uppercase hover:bg-primary/90 transition-all w-full sm:w-auto"
            >
              <BookOpen className="h-4 w-4" /> Read the Book Now
            </a>
            <div className="mt-8 border-t border-border pt-6 text-left">
              <p className="eyebrow mb-2">Prefer a paperback?</p>
              <p className="text-sm text-muted-foreground font-light leading-relaxed mb-4">
                Book a free, no-obligation, in-person appointment with John, and he'll bring you a complimentary
                100+ page book—a $19.99 value.
              </p>
              <AppointmentDialog
                trigger={
                  <button
                    type="button"
                    className="inline-flex items-center gap-2 text-primary text-xs tracking-[0.25em] uppercase border-b border-primary pb-1 hover:gap-3 transition-all"
                  >
                    Book an Appointment <ArrowRight className="h-4 w-4" />
                  </button>
                }
              />
            </div>
          </div>
        ) : (
          <>
            <DialogHeader>
              <p className="eyebrow mb-2">Free Digital Copy</p>
              <DialogTitle className="font-serif text-2xl md:text-3xl font-light leading-tight">
                Get your free book
              </DialogTitle>
              <DialogDescription className="text-muted-foreground font-light">
                Tell us where to reach you and the book opens right away. The digital copy is free; no appointment
                needed.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={onSubmit} className="space-y-4 mt-2" noValidate>
              <div>
                <label htmlFor="br-book" className={labelCls}>Book</label>
                <select id="br-book" name="book" value={form.book} onChange={onChange} className={inputCls}>
                  {BOOKS.map((b) => (
                    <option key={b.key} value={b.key}>{b.title}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="br-fn" className={labelCls}>First Name *</label>
                  <input id="br-fn" name="firstName" value={form.firstName} onChange={onChange} autoComplete="given-name" maxLength={60} className={inputCls} aria-invalid={!!errors.firstName} />
                  {errors.firstName && <p className={errorCls}>{errors.firstName}</p>}
                </div>
                <div>
                  <label htmlFor="br-ln" className={labelCls}>Last Name *</label>
                  <input id="br-ln" name="lastName" value={form.lastName} onChange={onChange} autoComplete="family-name" maxLength={60} className={inputCls} aria-invalid={!!errors.lastName} />
                  {errors.lastName && <p className={errorCls}>{errors.lastName}</p>}
                </div>
              </div>
              <div>
                <label htmlFor="br-em" className={labelCls}>Email *</label>
                <input id="br-em" type="email" name="email" value={form.email} onChange={onChange} autoComplete="email" maxLength={255} className={inputCls} aria-invalid={!!errors.email} />
                {errors.email && <p className={errorCls}>{errors.email}</p>}
              </div>
              <div>
                <label htmlFor="br-ph" className={labelCls}>Phone <span className="text-muted-foreground font-normal normal-case tracking-normal">(optional)</span></label>
                <input id="br-ph" type="tel" name="phone" value={form.phone} onChange={onChange} autoComplete="tel" maxLength={30} className={inputCls} aria-invalid={!!errors.phone} />
                {errors.phone && <p className={errorCls}>{errors.phone}</p>}
              </div>
              {form.phone.trim() !== "" && (
                <label htmlFor="br-sms" className="flex items-start gap-3 cursor-pointer">
                  <input id="br-sms" name="smsConsent" type="checkbox" checked={form.smsConsent} onChange={onChange} className="mt-1 h-4 w-4 shrink-0 accent-primary" />
                  <span className="text-xs leading-relaxed text-muted-foreground font-light">
                    I agree to receive calls and text messages from John Mastroianni | Align Right Realty - Homekey
                    about my book request and real estate questions. Message frequency varies. Message and data rates
                    may apply. Reply STOP to opt out or HELP for help. Consent is not a condition of purchase. See our{" "}
                    <Link to="/privacy-policy" className="text-primary hover:underline">Privacy Policy</Link> and{" "}
                    <Link to="/terms-and-conditions" className="text-primary hover:underline">Terms &amp; Conditions</Link>.
                  </span>
                </label>
              )}
              <button
                type="submit"
                disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-3 bg-primary text-primary-foreground px-8 py-4 text-xs font-bold tracking-[0.3em] uppercase hover:bg-primary/90 transition-all disabled:opacity-50"
              >
                {submitting ? "Sending…" : "Get My Free Digital Copy"} <BookOpen className="h-4 w-4" />
              </button>
              <p className="text-[11px] text-muted-foreground font-light text-center">
                Your information stays with John and is used only to follow up on your request.
              </p>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BookRequestDialog;
